import React, { useContext } from 'react';
import i18next from 'core/i18n';

import { SmallTitle } from '@indigo-ag/component-module';
import { AgFinancingFileFolder, AgGuarantorProcess, CreditInput } from '@indigo-ag/schema';

import { EnhancedExpansionPanel } from 'shared';
import {
  ClientDataSection,
  ExploredAreas,
  FinancialInformationSection,
  IncomeStatementSection,
  JudicialActionsSection,
  NotesSection,
  Participation,
  PesquisaSerasa,
  ScoreSection,
  creditUtils
} from 'features/brazil-grower/components/BrazilGrowerFinance/shared/credit-analysis';
import { uploadFilesToAgFinancing } from 'features/brazil-grower/components/BrazilGrowerFinance/utils';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { getPesquisaSerasaInfoFromCredit } from 'features/brazil-grower/components/BrazilGrowerFinance/shared/credit-analysis/shared/utils';

export interface GuarantorCreditAnalysisProps {
  guarantorProcess: AgGuarantorProcess;
  updateGuarantorProcess: (updatedBC: Partial<CreditInput>) => Promise<unknown>;
  disabled?: boolean;
}

export const GuarantorCreditAnalysis: React.FunctionComponent<GuarantorCreditAnalysisProps> = ({
  guarantorProcess,
  updateGuarantorProcess,
  disabled = false
}) => {
  const { creditAnalysis } = guarantorProcess;

  /**
   * Finance Process Context
   */
  const { agFinancingId, contactRolesBackgroundChecksQuery } = useContext(FinanceProcessContext);
  const contactRoles = contactRolesBackgroundChecksQuery?.data?.opportunityContactRoles;
  const serasaDataApi = contactRolesBackgroundChecksQuery?.data?.serasaReport;
  const hasSerasaApiData = !!serasaDataApi;

  const uploadFilesToCredit = uploadFilesToAgFinancing({
    agFinancingId,
    folder: AgFinancingFileFolder.CREDIT
  });

  const getSerasaData = () => {
    if (serasaDataApi) {
      return creditUtils.mapSerasaReportToCreditInput(serasaDataApi, contactRoles);
    }

    return getPesquisaSerasaInfoFromCredit(creditAnalysis);
  };

  return (
    <EnhancedExpansionPanel
      title={<SmallTitle className="text-transform-capitalize">{i18next.t('creditAnalysis')}</SmallTitle>}
    >
      <div className="width-100">
        <Participation
          participationInfo={creditUtils.getParticipationInfoFromCredit(creditAnalysis)}
          disabled={disabled}
          updateCredit={updateGuarantorProcess}
        />
        <ClientDataSection
          clientDataInfo={creditUtils.getClientDataInfoFromCredit(creditAnalysis)}
          disabled={disabled}
          updateCredit={updateGuarantorProcess}
        />
        <PesquisaSerasa
          pesquisaSerasaInfo={getSerasaData()}
          disabled={disabled || hasSerasaApiData}
          updateCredit={updateGuarantorProcess}
          toUploadFiles={uploadFilesToCredit}
        />
        <JudicialActionsSection
          judicialActionsInfo={creditUtils.getJudicialActionsInfoFromCredit(creditAnalysis)}
          disabled={disabled}
          updateCredit={updateGuarantorProcess}
        />
        <IncomeStatementSection
          incomeStatementInfo={creditUtils.getIncomeStatementInfoFromCredit(creditAnalysis)}
          disabled={disabled}
          updateCredit={updateGuarantorProcess}
        />
        <ExploredAreas
          exploredAreasInfo={creditUtils.getExploredAreasInfoFromCredit(creditAnalysis)}
          disabled={disabled}
          updateCredit={updateGuarantorProcess}
        />
        <FinancialInformationSection
          financialInformationInfo={creditUtils.getFinancialInfoFromCredit(creditAnalysis)}
          disabled={disabled}
          updateCredit={updateGuarantorProcess}
        />
        <ScoreSection
          scoreSectionInfo={creditUtils.getConsultingScoreInfoFromCredit(creditAnalysis)}
          disabled={disabled}
          updateCredit={updateGuarantorProcess}
        />
        <NotesSection
          notesSectionInfo={creditUtils.getNotesInfoFromCredit(creditAnalysis)}
          disabled={disabled}
          updateCredit={updateGuarantorProcess}
        />
      </div>
    </EnhancedExpansionPanel>
  );
};
